import { Fragment } from "react";
import { ExternalLinkIcon, LetterCloseIcon, PhoneLandlineIcon } from "assets";
import { IAgency } from "models";
import { getUrlWithProtocol } from "shared/utils/getUrlWithProtocol";
import { parsePhoneNumberList, sanitizePhoneNumberForTel } from "shared-utils";
import { dataTestIds } from "data-testids";

interface IAgentContactBlockProps {
  agent: IAgency;
  address: string;
  phoneNumbers: string;
}

const displayPhoneLandlineIcon = (index: number) => {
  return index === 0 ? (
    <PhoneLandlineIcon className="h-4 w-4 translate-y-[3px]" />
  ) : null;
};

const renderPhoneNumbers = (phoneNumbers: string) => {
  const parsedPhoneNumberList = parsePhoneNumberList(phoneNumbers);

  return (
    <>
      {parsedPhoneNumberList.map((item, index) => (
        <Fragment key={`${item.number}-${index}`}>
          {item.isValid ? (
            <a
              href={`tel:${sanitizePhoneNumberForTel(item.number)}`}
              className="inline-flex items-baseline gap-1 font-medium text-color-brand-one hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
              data-testid={`${dataTestIds.profile["Profile.Agency.agencyDetails.phone"]}`}
            >
              {displayPhoneLandlineIcon(index)}
              {item.number}
            </a>
          ) : (
            <span
              className="inline-flex items-baseline gap-1 font-medium dark:text-white"
              data-testid={`${dataTestIds.profile["Profile.Agency.agencyDetails.phone"]}`}
            >
              {displayPhoneLandlineIcon(index)}
              {item.number}
            </span>
          )}
          {index < parsedPhoneNumberList.length - 1 && (
            <span className="font-medium">, </span>
          )}
        </Fragment>
      ))}
    </>
  );
};

const AgentContactBlock = ({
  agent,
  address,
  phoneNumbers,
}: IAgentContactBlockProps) => {
  return (
    <>
      {agent.description && <p className="text-s">{agent.description}</p>}

      {agent.website ? (
        <a
          className="group flex items-center gap-2 text-color-brand-one hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
          href={getUrlWithProtocol(agent.website)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${dataTestIds.profile["Profile.Agency.agencyDetails.name"]}`}
        >
          <h2 className="text-s font-semibold leading-4">{agent.name}</h2>

          <ExternalLinkIcon className="invisible group-hover:visible" />
        </a>
      ) : (
        <div
          className="mb-1 text-color-brand-one dark:text-blue-aquamarine"
          data-testid={`${dataTestIds.profile["Profile.Agency.agencyDetails.name"]}`}
        >
          <h2 className="text-s font-semibold leading-4">{agent.name}</h2>
        </div>
      )}

      {address && (
        <p
          className="text-s"
          data-testid={`${dataTestIds.profile["Profile.Agency.agencyDetails.address"]}`}
        >
          {address}
        </p>
      )}

      {phoneNumbers.length > 0 && (
        <div className="flex items-start gap-2 dark:text-white/90">
          <div className="text-s">{renderPhoneNumbers(phoneNumbers)}</div>
        </div>
      )}

      {agent.email && (
        <a
          href={`mailto:${agent.email}`}
          className="flex items-center gap-2 font-medium text-color-brand-one break-word hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
          data-testid={`${dataTestIds.profile["Profile.Agency.agencyDetails.email"]}`}
        >
          <LetterCloseIcon width={13} className="shrink-0" />
          {agent.email}
        </a>
      )}
    </>
  );
};

export default AgentContactBlock;
